// svgMetrics.js
import {
  calculateRectanglePerimeter,
  calculateRectangleArea,
  calculateCircleCircumference,
  calculateCircleArea,
  calculateTextMetrics,
  calculatePathArea,
  calculatePolygonPerimeter,
  calculatePolygonAreaFromPoints,
  convertStrokeWidth, calculateEllipsePerimeter, calculateEllipseArea, calculatePolylineLength,
  getElementScale
} from './svgMetricsUtils'

// ── High-density thresholds (PRICING PARAMETERS — changing these changes what buyers pay) ──
//
// What the flag is FOR (Nedim, 2026-07-31): "very many lines that are tight to each other" are
// harder to make — for contour cutting AND for CNC pocketing — than a simple piece with no such
// detail. So it must measure DETAIL PACKED CLOSE TOGETHER, not merely a large drawing.
//
// Three independent signals, any of which marks a design dense:
//
// 1. LINE WORK PER PIECE AREA — the primary signal, and the one that captures "tight to each
//    other". mm of cut/engrave line per mm² of the piece. Lines spaced 1mm apart give ~1.0,
//    2mm apart ~0.5, 5mm apart ~0.2. Three ordinary shapes on a 100x110 piece give ~0.06.
//    0.35 corresponds to detail roughly 3mm apart.
// 2. PIECES PER AREA — totalLineLength / sqrt(totalCutOutArea) is scale-invariant and grows as
//    √(piece count): one square ≈ 4, 100 equal squares ≈ 40. Catches many separate cut-outs
//    even when each is individually simple.
// 3. RAW ELEMENT COUNT — a blunt backstop.
//
// Signal 1 is essential because signals 2 and 3 both miss thin OPEN line work (no enclosed
// area, few elements) — e.g. 40 closely-spaced engraved strokes, which is exactly the
// expensive case.
const HIGH_DENSITY_LINE_PER_AREA_MM = 0.35   // mm of line per mm² of piece
const HIGH_DENSITY_PIECES_RATIO = 40         // totalLineLength / sqrt(totalCutOutArea)
const HIGH_DENSITY_ELEMENT_COUNT = 100
const MM_PER_INCH = 25.4

export function collectSvgMetrics (svgEditor, pricingComplexity) {
  const svgCanvas = svgEditor.svgCanvas;
  const isMetric = pricingComplexity.isMetric;
  const dpi = 96;
  const pixelToMm = 25.4 / dpi;
  const conversionFactor = isMetric ? pixelToMm : 1 / dpi;
  console.log(`pricingComplexity.strokeWidthThreshold: ${pricingComplexity.strokeWidthThreshold}`);

  let metrics = {
    elementCount: 0,
    totalLineLength: 0,
    totalCutOutArea: 0,
    segmentCount: 0,
    elementDescriptions: {},
    isHighDensity: false,
  };

  const svgContent = svgCanvas.getSvgContent();
  const processElement = (element) => {
    const type = element.tagName.toLowerCase();

    // Definitions and metadata hold things that are referenced, not drawn — a <rect> in <defs> is
    // never cut. Walking into them counted it as an element (+0,50 € on the RP-0003 front template).
    if (['defs', 'title', 'metadata'].includes(type)) {
      return;
    }

    if (['svg', 'g'].includes(type)) {
      Array.from(element.children).forEach(processElement);
      return;
    }

    metrics.elementCount++;
    metrics.elementDescriptions[type] = (metrics.elementDescriptions[type] || 0) + 1;

    // Account for cumulative transforms (e.g. a scale() on an ancestor <g> or the
    // element's own transform). Without this, measurements are taken in the element's
    // local coordinate system and the resulting price changes depending on whether the
    // design is grouped/ungrouped or has baked-in transforms.
    const zoom = typeof svgCanvas.getZoom === 'function' ? svgCanvas.getZoom() : 1;
    const scale = getElementScale(element, zoom);
    const lengthScale = (scale.sx + scale.sy) / 2;
    const areaScale = scale.sx * scale.sy;

    switch (type) {
      case 'rect':
        const rectPerimeter = calculateRectanglePerimeter(element, conversionFactor, isMetric, dpi) * lengthScale;
        const rectArea = calculateRectangleArea(element, pricingComplexity, isMetric, dpi) * areaScale;
        metrics.totalLineLength += rectPerimeter;
        if (rectArea > 0) {
          metrics.totalCutOutArea += rectArea; // Directly update totalCutOutArea here
          console.log(`Updated RECT totalCutOutArea: ${rectArea}`); // Log new totalCutOutArea
        }
        console.log(`Rectangle Perimeter: ${rectPerimeter}, Area: ${rectArea}`);
        break;
      case 'circle':
        const circleCircumference = calculateCircleCircumference(element, isMetric, dpi) * lengthScale;
        const circleArea = calculateCircleArea(element, pricingComplexity, isMetric, dpi) * areaScale;
        metrics.totalLineLength += circleCircumference;
        if (circleArea > pricingComplexity.minimumCutOutArea) {
          metrics.totalCutOutArea += circleArea;
          console.log(`Updated CIRCLE totalCutOutArea: ${circleArea}`);
        }
        console.log(`Circle Circumference: ${circleCircumference}, Area: ${circleArea}`);
        break;
      case 'path':
        const length = (element.getTotalLength ? element.getTotalLength() * conversionFactor : 0) * lengthScale;
        metrics.totalLineLength += length;
        console.log(`Path segment length (inches): ${length}`);
        const pathArea = calculatePathArea(element, pricingComplexity, isMetric, dpi) * areaScale;
        metrics.totalCutOutArea += pathArea;
        console.log(`Updated PATH totalCutOutArea: ${pathArea}`);
        break;
      case 'polygon':
        const polygonPerimeter = calculatePolygonPerimeter(element, conversionFactor) * lengthScale;
        metrics.totalLineLength += polygonPerimeter;
        const polygonArea = calculatePolygonArea(element, pricingComplexity, conversionFactor, isMetric, dpi) * areaScale;
        metrics.totalCutOutArea += polygonArea;
        console.log(`Polygon Perimeter: ${polygonPerimeter}`);
        console.log(`Updated POLYGON totalCutOutArea: ${polygonArea}`);
        break;
      case 'line':
        metrics.totalLineLength += calculateLineLength(element, conversionFactor) * lengthScale;
        break;
      case 'text':
        const textMetrics = calculateTextMetrics(element, pricingComplexity, conversionFactor, isMetric, dpi);
        metrics.totalLineLength += textMetrics.lineLength * lengthScale;
        metrics.totalCutOutArea += textMetrics.cutOutArea * areaScale;
        console.log(`Text Element Outline Length: ${textMetrics.lineLength}, Area: ${textMetrics.cutOutArea}`);
        break;
      case 'ellipse':
        const ellipsePerimeter = calculateEllipsePerimeter(element, conversionFactor, pricingComplexity) * lengthScale;
        if (ellipsePerimeter > 0) { // Only add if perimeter is valid
          metrics.totalLineLength += ellipsePerimeter;
        }
        break;
      case 'polyline':
        const polylineLength = calculatePolylineLength(element, conversionFactor) * lengthScale;
        metrics.totalLineLength += polylineLength;
        break;
      default:
        console.log(`Unprocessed element type: ${type}`);
    }
  };

  Array.from(svgContent.children).forEach(processElement);

  // See the threshold block at the top of this file for what "high density" means and why.
  // The previous test was `totalLineLength > 500` — an ABSOLUTE threshold with no reference to
  // the piece size, so any sufficiently large drawing tripped it. A single rectangle among three
  // simple shapes was flagged dense and charged the surcharge (BUG-35).

  // Signal 1 — line work per unit of piece area. Captures "many lines tight to each other".
  // getResolution() is in canvas pixels; conversionFactor converts to the same unit as
  // totalLineLength (mm when metric, inches otherwise), so the ratio is unit-consistent.
  let pieceArea = 0;
  try {
    const res = typeof svgCanvas.getResolution === 'function' ? svgCanvas.getResolution() : null;
    if (res && res.w > 0 && res.h > 0) {
      pieceArea = (res.w * conversionFactor) * (res.h * conversionFactor);
    }
  } catch (e) {
    pieceArea = 0;
  }
  const linePerArea = pieceArea > 0 ? metrics.totalLineLength / pieceArea : 0;
  const linePerAreaThreshold = isMetric
    ? HIGH_DENSITY_LINE_PER_AREA_MM
    : HIGH_DENSITY_LINE_PER_AREA_MM * MM_PER_INCH;

  // Signal 2 — many separate cut-outs, each possibly simple. Scale-invariant.
  const piecesRatio = metrics.totalCutOutArea > 0
    ? metrics.totalLineLength / Math.sqrt(metrics.totalCutOutArea)
    : 0;

  metrics.isHighDensity =
    linePerArea > linePerAreaThreshold ||
    piecesRatio > HIGH_DENSITY_PIECES_RATIO ||
    metrics.elementCount > HIGH_DENSITY_ELEMENT_COUNT;

  console.log(`density: linePerArea=${linePerArea.toFixed(4)} (limit ${linePerAreaThreshold}), ` +
              `piecesRatio=${piecesRatio.toFixed(2)} (limit ${HIGH_DENSITY_PIECES_RATIO}), ` +
              `elements=${metrics.elementCount} -> isHighDensity=${metrics.isHighDensity}`);

  return metrics;
}

function calculatePolygonArea(polygon, pricingComplexity, conversionFactor, isMetric, dpi) {
  const points = polygon.getAttribute('points');
  if (!points) {
    console.error("Polygon has no 'points' attribute:", polygon);
    return 0;
  }
  const pointPairs = points.split(' ').map(pair => {
    const [x, y] = pair.split(',').map(coord => parseFloat(coord) * conversionFactor);
    return { x, y };
  });
  if (pointPairs.length < 3) {
    console.error("Not enough points for polygon area calculation:", pointPairs);
    return 0;
  }
  const strokeWidth = convertStrokeWidth(polygon.getAttribute('stroke-width'), pricingComplexity.isMetric, dpi);
  const isFilled = polygon.getAttribute('fill') !== 'none';
  let perimeter = 0;
  for (let i = 0; i < pointPairs.length; i++) {
    const start = pointPairs[i];
    const end = pointPairs[(i + 1) % pointPairs.length];
    perimeter += Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
  }
  const polygonArea = calculatePolygonAreaFromPoints(pointPairs);
  if (isFilled) {
    return polygonArea;
  } else if (strokeWidth >= pricingComplexity.strokeWidthThreshold) {
    return perimeter * strokeWidth;
  }
  return 0;
}

function calculateLineLength(line, conversionFactor) {
  const x1 = parseFloat(line.getAttribute('x1')) || 0;
  const y1 = parseFloat(line.getAttribute('y1')) || 0;
  const x2 = parseFloat(line.getAttribute('x2')) || 0;
  const y2 = parseFloat(line.getAttribute('y2')) || 0;

  console.log(`Parsed coordinates: x1=${x1}, y1=${y1}, x2=${x2}, y2=${y2}`);
  console.log(`Conversion factor: ${conversionFactor}`);
  const dx = (x2 - x1) * conversionFactor;
  const dy = (y2 - y1) * conversionFactor;
  const length = Math.sqrt(dx * dx + dy * dy);
  console.log(`Calculated line length: ${length}`);
  return length;
}

